import React, {useMemo} from "react";
import CommonPagination from "../table/movable/CommonPagination";
import {
    formatLinkedCellText,
    getNestedValue,
    resolvePrimaryColumnKey,
} from "../detailLayout/detailLinkedListCell";
import "./simmsListThumb.css";

const HISTORY_TYPE_LABELS = {
    C: "등록",
    U: "수정",
    D: "삭제",
};

function isHistoryTypeColumn(col) {
    const key = String(col?.key ?? col?.field ?? "").split(".").pop();
    return key === "type" || col?.label === "타입";
}

function formatHistoryTypeValue(value) {
    const code = String(value ?? "").trim().toUpperCase();
    return HISTORY_TYPE_LABELS[code] ?? value;
}

function formatThumbCellText(row, col, rowIndex) {
    if (isHistoryTypeColumn(col)) {
        const raw = getNestedValue(row, col.key, col.short);
        return formatHistoryTypeValue(raw);
    }

    if (typeof col.render === "function") {
        const value = getNestedValue(row, col.key, col.short);
        try {
            const rendered = col.render({
                row,
                value,
                columnKey: col.key,
                rowIndex,
                stopRowClick: () => {},
            });
            if (rendered != null && rendered !== "") {
                return typeof rendered === "string" || typeof rendered === "number"
                    ? String(rendered)
                    : formatLinkedCellText(row, col);
            }
        } catch {
            /* fall through */
        }
    }

    return formatLinkedCellText(row, col);
}

function normalizeListPayload(data) {
    if (Array.isArray(data)) {
        return {rows: data, pageable: null};
    }
    return {
        rows: data?.content ?? [],
        pageable: data?.pageable ?? null,
        totalPages: data?.totalPages,
        totalElements: data?.totalElements,
    };
}

function buildPaginationPage(pageable, totalPages, totalElements) {
    if (!pageable) return null;
    return {
        currentPage: pageable.pageNumber ?? 0,
        totalPages: totalPages ?? pageable.totalPages ?? 0,
        size: pageable.pageSize ?? pageable.size ?? 20,
        totalElements: totalElements ?? pageable.totalElements ?? 0,
    };
}

export default function SimmsListThumbView({
    data,
    header = [],
    onRowClick,
    onPageChange,
    loading = false,
    emptyMessage = "조회된 데이터가 없습니다.",
}) {
    const {rows, pageable, totalPages, totalElements} = useMemo(
        () => normalizeListPayload(data),
        [data]
    );

    const columns = useMemo(() => header ?? [], [header]);
    const primaryKey = useMemo(() => resolvePrimaryColumnKey(columns), [columns]);
    const metaColumns = useMemo(
        () => columns.filter((col) => col.key !== primaryKey).slice(0, 5),
        [columns, primaryKey]
    );
    const primaryCol = useMemo(
        () => columns.find((col) => col.key === primaryKey) ?? columns[0],
        [columns, primaryKey]
    );

    const page = useMemo(
        () => buildPaginationPage(pageable, totalPages, totalElements),
        [pageable, totalPages, totalElements]
    );

    const handlePageChange = (pageIndex) => {
        if (!onPageChange) return;
        onPageChange({
            pageNumber: pageIndex,
            pageSize: pageable?.pageSize ?? pageable?.size ?? 20,
            sort: pageable?.sort,
            sortDirection: pageable?.sortDirection,
        });
    };

    if (loading && rows.length === 0) {
        return (
            <div className="simms-list-thumb" role="status">
                <div className="simms-list-thumb__loading">불러오는 중…</div>
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="simms-list-thumb" role="status">
                <div className="simms-list-thumb__empty">{emptyMessage}</div>
            </div>
        );
    }

    return (
        <div className="simms-list-thumb list-container">
            <div className="asset-thumbnail-container simms-list-thumb__scroll">
                {rows.map((row, rowIndex) => {
                    const title = primaryCol ? formatThumbCellText(row, primaryCol, rowIndex) : "";
                    const interactive = typeof onRowClick === "function";
                    const rowProps = interactive
                        ? {
                              role: "button",
                              tabIndex: 0,
                              onClick: () => onRowClick(row),
                              onKeyDown: (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      onRowClick(row);
                                  }
                              },
                          }
                        : {};

                    return (
                        <div
                            key={row.id ?? rowIndex}
                            className={`asset-thumb-item simms-list-thumb__item${interactive ? " is-clickable" : ""}`}
                            {...rowProps}
                        >
                            <div className="simms-list-thumb__title">{title || "—"}</div>
                            {metaColumns.length > 0 ? (
                                <dl className="asset-linked-list-meta simms-list-thumb__meta">
                                    {metaColumns.map((col) => {
                                        const value = formatThumbCellText(row, col, rowIndex);
                                        if (!value) return null;
                                        return (
                                            <div key={col.key} className="asset-linked-list-meta-item">
                                                <dt>{col.label}</dt>
                                                <dd>{value}</dd>
                                            </div>
                                        );
                                    })}
                                </dl>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            {page && onPageChange ? (
                <CommonPagination page={page} onPageChange={handlePageChange} pageRangeDisplayed={5} />
            ) : null}
        </div>
    );
}
